import { Table, TableBody, TableCell, TableHead, TableRow } from "~/common/components/Table/TableElements";
import { LinkButton, LinkType } from "~/common/components/Link";
import { useBooleanState } from "~/common/hooks/useBooleanState";
import LabelBox from "~/domains/salesCampaign/components/LabelBox/LabelBox";
import {
  type CaseDataObject,
  CaseIcon,
  CaseStatus,
} from "~/features/orders/orderDetails/components/ServiceCases/types";
import CircleSpinner from "~/common/components/CircleSpinner/CircleSpinner";
import { type Dispatch, type SetStateAction, useEffect, useState } from "react";
import {
  addCommentByCode,
  fetchServiceCasesByLineNumber,
} from "~/features/orders/orderDetails/components/ServiceCases/api";
import AddCommentModal from "~/features/orders/orderDetails/components/ServiceCases/AddCommentModal";
import { t } from "i18next";
import { CreateCaseElement } from "~/features/orders/common/components/CreateCaseElement";
import { Alert } from "~/common/mui/Alert/Alert";
import { AlertTitle, styled } from "~/common/mui";
import { C_SURFACE, C_WHITE } from "~/common/components/icon/Colors";
import AddCommentIcon from "@mui/icons-material/AddComment";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";

const StyledTable = styled(Table)({
  width: "800px !important",
  [`& .odd:nth-of-type(2n)`]: {
    background: `${C_SURFACE} !important`,
  },
  [`& .odd:nth-of-type(2n + 1)`]: {
    background: "transparent !important",
  },
  [`& .even:nth-of-type(2n)`]: {
    background: `${C_WHITE} !important`,
  },
  "& tbody tr td": {
    verticalAlign: "middle",
    "& p": {
      margin: 0,
      verticalAlign: "middle",
    },
  },
  "& td:last-child": {
    padding: "10px 8px !important",
  },
});

const HeaderCell = styled(TableCell)({
  color: "var(--c-on-surface) !important",
  fontWeight: "bold !important",
  padding: "10px 8px !important",
});

const StatusBody = styled(TableCell)({
  width: "300px",
  display: "flex !important",
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "40px",
});

const CaseNumberCell = styled(TableCell)({
  width: "150px",
  fontWeight: "bold !important",
});

const ShowMoreContainer = styled("div")({
  width: "100%",
  display: "flex",
  justifyContent: "center",
});

const StatusLabelBox = styled(LabelBox)({
  fontWeight: "normal",
  color: C_WHITE,
  gap: "4px",
  alignItems: "center",
});

const LoadingContainer = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: theme.spacing(1.25),
}));

const LoadingMessage = styled(Alert)({
  background: `${C_WHITE} !important`,
  width: "100%",
});

const AddCommentText = styled("div")({
  textAlign: "start",
});

interface ServiceCasesTableProps {
  openPopup: () => void;
  orderNumber?: string;
  serviceCase?: CaseDataObject[];
  lineNumber?: string;
  className?: string;
  userHasCreatedCase: boolean;
  setSnackbar: Dispatch<SetStateAction<{ open: boolean; message: string }>>;
  isOdd: boolean;
}

const LIST_SIZE = 3;

const ServiceCasesTable = ({
  serviceCase,
  orderNumber,
  userHasCreatedCase,
  lineNumber,
  openPopup,
  setSnackbar,
  isOdd,
  className,
}: ServiceCasesTableProps) => {
  const { val: displayMore, toggle: toggleShowMore } = useBooleanState(false);
  const [commentModalCode, setCommentModalCode] = useState<string | undefined>(undefined);
  const [caseData, setCaseData] = useState<CaseDataObject[] | undefined>(serviceCase);

  const isCaseProcessing = caseData?.some((item) => !item.completeData);

  const showButton = caseData && caseData?.filter((item) => item.completeData).length > 3;

  const onAddCommentAction = (code: string, message: string) => {
    addCommentByCode(code, message);
    setSnackbar({
      open: true,
      message: t("serviceCases.commentSent.snackbarInfo"),
    });
  };

  useEffect(() => {
    if (isCaseProcessing && orderNumber && lineNumber) {
      //Make call every 2 minutes
      const interval = setInterval(
        () => {
          fetchServiceCasesByLineNumber(orderNumber, lineNumber).then((res) => {
            setCaseData(res[lineNumber]);
          });
        },
        1000 * 60 * 2,
      );
      return () => clearInterval(interval);
    }
  }, [isCaseProcessing]);

  const serviceCaseItems = caseData
    ?.filter((item) => item.completeData)
    .slice(0, displayMore ? undefined : LIST_SIZE)
    .map((item) => (
      <TableRow className={isOdd ? "odd" : "even"}>
        <CaseNumberCell>
          <LinkButton onClick={() => setCommentModalCode(item.code)} variant={LinkType.SECONDARY}>
            {item.sfCaseNumber}
          </LinkButton>
        </CaseNumberCell>
        <TableCell>
          <p>{item.reasonOfRequest}</p>
        </TableCell>
        <StatusBody>
          <StatusLabelBox variant="darkGrey">
            {(() => {
              const StatusIcon = CaseIcon[item.status];
              return <StatusIcon sx={{ fontSize: 10 }} />;
            })()}
            {t(CaseStatus[item.status])}
          </StatusLabelBox>

          <LinkButton onClick={() => setCommentModalCode(item.code)} iconBefore={<AddCommentIcon  />}>
            <AddCommentText>{t("page.wishlist.comment.add")}</AddCommentText>
          </LinkButton>
        </StatusBody>
      </TableRow>
    ));

  const loadingBanner = (
    <TableCell colSpan={9}>
      <LoadingContainer>
        <CircleSpinner />
        <LoadingMessage severity="info">
          <AlertTitle>
            {userHasCreatedCase ? t("serviceCases.caseCreated.create") : t("serviceCases.caseLoading.processing")}
          </AlertTitle>
          {userHasCreatedCase ? t("serviceCases.caseCreated.label") : t("serviceCases.caseLoading.label")}
        </LoadingMessage>
      </LoadingContainer>
    </TableCell>
  );

  return (
    <>
      {commentModalCode && (
        <AddCommentModal
          onSend={onAddCommentAction}
          caseData={caseData?.find((item) => item.code === commentModalCode)}
          onClose={() => setCommentModalCode(undefined)}
        />
      )}
      <CreateCaseElement openPopup={openPopup} />

      <StyledTable className={className}>
        {caseData && (
          <TableHead>
            <TableRow>
              <HeaderCell>{t("addComment.caseNumber")}</HeaderCell>
              <HeaderCell>{t("addComment.subject")}</HeaderCell>
              <HeaderCell>{t("page.openorders.search.orderLineStatus")}</HeaderCell>
            </TableRow>
          </TableHead>
        )}
        <TableBody>
          {(isCaseProcessing || userHasCreatedCase) && loadingBanner}
          {serviceCaseItems}
        </TableBody>
        <TableRow>
          <TableCell colSpan={12}>
            <ShowMoreContainer>
              {showButton && (
                <LinkButton
                  iconBefore={
                    displayMore ? (
                      <KeyboardArrowUpIcon  />
                    ) : (
                      <KeyboardArrowDownIcon  />
                    )
                  }
                  onClick={toggleShowMore}
                >
                  {t(`searchPage.refinements.${displayMore ? "showLess" : "showMore"}`)}
                </LinkButton>
              )}
            </ShowMoreContainer>
          </TableCell>
        </TableRow>
      </StyledTable>
    </>
  );
};

export default ServiceCasesTable;
